import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Shield, RefreshCw, Star } from "lucide-react";

// ── Friendly error messages ───────────────────────────────────────────────────

const ERROR_MAP = {
  INVALID_CREDENTIALS: "Incorrect email or password. Please try again.",
  USER_NOT_FOUND:      "No account found with this email.",
  ACCOUNT_DISABLED:    "Your account has been disabled. Please contact support.",
  TOO_MANY_REQUESTS:   "Too many attempts. Please wait a moment and try again.",
};

function friendlyError(detail) {
  if (!detail) return "Sign in failed. Please try again.";
  if (typeof detail !== "string") return "Sign in failed. Please try again.";
  return ERROR_MAP[detail] || detail;
}

// ── Trust badges ──────────────────────────────────────────────────────────────

const TRUST_ITEMS = [
  { icon: Shield,    label: "92.5 Certified" },
  { icon: Star,      label: "4.9 · Average Rating" },
  { icon: RefreshCw, label: "Easy Returns" },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
      toast.success("Welcome back!");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(friendlyError(detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    let redirectUrl;

    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      redirectUrl = 'http://localhost:3000/';
    } else if (window.location.hostname.includes('rivorajewels.com')) {
      redirectUrl = 'https://rivorajewels.com/';
    } else {
      redirectUrl = window.location.origin + '/';
    }

    // Keep the URL minimal — no custom state param that could confuse Emergent's auth service
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;

    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen flex">

      {/* ── LEFT — lifestyle image panel (desktop only) ───────────────── */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col overflow-hidden">
        {/* Different image from the homepage hero — intimate jewellery close-up */}
        <img
          src="https://images.unsplash.com/photo-1611591437281-460bfbe1220a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
          alt="Rivora silver jewellery craftsmanship"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/85 via-zinc-900/25 to-zinc-900/35" />

        {/* Top — brand name on image */}
        <div className="relative z-10 p-10">
          <div className="cormorant text-2xl font-light tracking-widest text-white opacity-90">
            RIVORA <span className="text-[#D4AF37]">JEWELS</span>
          </div>
        </div>

        {/* Bottom — brand quote */}
        <div className="relative z-10 mt-auto p-10 pb-14">
          <div className="w-10 h-0.5 bg-[#D4AF37] mb-6" aria-hidden="true" />
          <blockquote className="cormorant text-3xl sm:text-4xl font-light italic text-white leading-snug mb-4">
            "Everyday elegance,<br />crafted in 92.5 silver."
          </blockquote>
          <p className="text-white/55 text-xs uppercase tracking-[0.2em]">
            Handcrafted in Jaipur · Est. 2026
          </p>
        </div>
      </div>

      {/* ── RIGHT — form panel ────────────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col bg-[#FAFAFA]">

        {/* Mobile — gold top accent bar */}
        <div className="lg:hidden w-full h-1 bg-[#D4AF37]" aria-hidden="true" />

        {/* Mobile — top nav */}
        <div className="lg:hidden flex items-center justify-between px-6 pt-5">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 uppercase tracking-widest transition-colors"
          >
            <ArrowLeft size={13} />
            Back to Shop
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md">

            {/* Mobile — logo */}
            <Link to="/" className="inline-block lg:hidden mb-8">
              <div className="cormorant text-2xl font-light tracking-widest text-zinc-900">RIVORA <span className="text-[#D4AF37]">JEWELS</span></div>
            </Link>

            {/* Desktop — back link */}
            <Link
              to="/"
              className="hidden lg:inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-700 uppercase tracking-widest transition-colors mb-8"
            >
              <ArrowLeft size={12} />
              Back to Shop
            </Link>

            {/* Heading */}
            <h2
              className="cormorant text-4xl font-light text-zinc-900 mt-2 mb-1"
              data-testid="login-title"
            >
              Sign In
            </h2>
            <p className="text-sm text-zinc-500 mb-8">
              Welcome back — your collection awaits.
            </p>

            {/* ── Form ────────────────────────────────────────────────── */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">

              {/* Email */}
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-zinc-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>

              {/* Password + show/hide toggle */}
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border border-zinc-300 bg-white px-4 py-3 pr-12 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  {/* FIX: Eye toggle */}
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors p-1"
                    aria-label={showPass ? "Hide password" : "Show password"}
                    data-testid="toggle-password"
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* FIX: Remember me + forgot password row */}
              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="sr-only"
                      data-testid="remember-me"
                    />
                    <div className={`w-4 h-4 border transition-colors flex items-center justify-center ${
                      remember
                        ? "bg-zinc-900 border-zinc-900"
                        : "border-zinc-300 bg-white group-hover:border-zinc-500"
                    }`}>
                      {remember && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-zinc-500">Remember me</span>
                </label>

                <Link
                  to="/forgot-password"
                  className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-3.5 uppercase tracking-widest text-xs transition-colors disabled:opacity-50 mt-2"
                data-testid="login-submit-button"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#FAFAFA] px-3 text-zinc-400 tracking-widest">Or</span>
              </div>
            </div>

            {/* Google sign in */}
            <button
              onClick={handleGoogleAuth}
              className="w-full border border-zinc-300 bg-white hover:bg-zinc-50 py-3 text-sm transition-colors flex items-center justify-center gap-2.5"
              data-testid="google-auth-button"
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </button>

            {/* Register link */}
            <p className="text-center text-sm text-zinc-500 mt-6">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-zinc-900 font-medium hover:underline underline-offset-2"
                data-testid="register-link"
              >
                Create one
              </Link>
            </p>

            {/* FIX: Trust badges below form */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8 pt-6 border-t border-zinc-100">
              {TRUST_ITEMS.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-zinc-400">
                  <Icon size={12} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[10px] uppercase tracking-wide whitespace-nowrap">
                    {label}
                  </span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}